import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { PageHeader } from "@/components/PageHeader";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { TourEditor } from "@/pages/super/TourEditor";
import { SeoEditor } from "@/pages/super/SeoEditor";
import { api, errMsg } from "@/lib/api";

const TEXT_FIELDS = [
  ["hero_eyebrow", "Hero eyebrow"], ["hero_title", "Hero headline"], ["hero_subtitle", "Hero subtitle", true], ["hero_cta", "Hero button text"],
  ["features_title", "Features heading"],
  ["care_eyebrow", "Care section label"], ["care_title", "Care section headline"], ["care_body", "Care section text", true], ["care_image", "Care section image URL"],
  ["pricing_eyebrow", "Pricing label"], ["pricing_title", "Pricing headline"], ["faq_title", "FAQ heading"], ["footer_text", "Footer text"],
];
const LISTS = [
  ["stats", "Stats", [["value", "Value"], ["label", "Label"]]],
  ["features", "Feature cards", [["title", "Title"], ["body", "Text"]]],
  ["faq", "FAQ", [["q", "Question"], ["a", "Answer"]]],
];

const ListEditor = ({ name, label, fields, items, onChange }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid={`cms-list-${name}`}>
    <div className="flex items-center justify-between">
      <h3 className="font-display text-lg font-semibold">{label}</h3>
      <Button size="sm" variant="outline" type="button" onClick={() => onChange([...items, {}])} data-testid={`cms-add-${name}`}><Plus className="h-4 w-4" /></Button>
    </div>
    <div className="mt-4 space-y-3">
      {items.map((it, i) => (
        <div key={i} className="flex gap-2">
          {fields.map(([k, l]) => (
            <Input key={k} placeholder={l} value={it[k] || ""} onChange={(e) => onChange(items.map((x, j) => (j === i ? { ...x, [k]: e.target.value } : x)))} data-testid={`cms-${name}-${i}-${k}`} />
          ))}
          <Button size="icon" variant="ghost" type="button" onClick={() => onChange(items.filter((_, j) => j !== i))} data-testid={`cms-remove-${name}-${i}`}><Trash2 className="h-4 w-4 text-rose-500" /></Button>
        </div>
      ))}
    </div>
  </div>
);

export default function SuperContent() {
  const [c, setC] = useState(null);
  useEffect(() => {
    api.get("/super/cms").then((r) => setC(r.data));
  }, []);
  if (!c) return null;

  const save = async () => {
    try {
      await api.put("/super/cms", { content: c });
      toast.success("Website content published");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="max-w-4xl">
      <PageHeader title="Site content" subtitle="Edit the StaffRotas.com home page, product tour, page titles and link previews." />
      <Tabs defaultValue="home">
        <TabsList className="mb-6">
          <TabsTrigger value="home" data-testid="cms-tab-home">Home page</TabsTrigger>
          <TabsTrigger value="tour" data-testid="cms-tab-tour">Product tour</TabsTrigger>
          <TabsTrigger value="seo" data-testid="cms-tab-seo">SEO &amp; sharing</TabsTrigger>
        </TabsList>
        <TabsContent value="tour"><TourEditor /></TabsContent>
        <TabsContent value="seo"><SeoEditor /></TabsContent>
        <TabsContent value="home">
      <div className="mb-6 flex justify-end gap-3">
        <a href="/" target="_blank" rel="noreferrer" className="self-center text-sm font-semibold text-indigo-600" data-testid="cms-preview-link">View site</a>
        <Button className="rounded-full" onClick={save} data-testid="cms-save-btn">Publish changes</Button>
      </div>
      <div className="space-y-6">
        <div className="space-y-4 rounded-xl border border-slate-200 bg-white p-6">
          {TEXT_FIELDS.map(([k, l, long]) => (
            <div key={k} className="space-y-1.5">
              <Label>{l}</Label>
              {long
                ? <Textarea value={c[k] || ""} onChange={(e) => setC({ ...c, [k]: e.target.value })} data-testid={`cms-${k}-input`} />
                : <Input value={c[k] || ""} onChange={(e) => setC({ ...c, [k]: e.target.value })} data-testid={`cms-${k}-input`} />}
            </div>
          ))}
        </div>
        {LISTS.map(([k, l, fields]) => (
          <ListEditor key={k} name={k} label={l} fields={fields} items={c[k] || []} onChange={(v) => setC({ ...c, [k]: v })} />
        ))}
      </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
